import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import { FiSend, FiMail, FiTrendingUp, FiUsers, FiSliders, FiPlus, FiSearch, FiChevronDown, FiArrowRight } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { CampaignRow, CommunicationsCampaignsResult, CommunicationsCampaignsSummaryResult } from '../../../model/CommunicationsCampaignsResult';
import api from '../../../api/api';
import { to } from '../../../util/util';

const pageSize = 10;
const tones: Record<string, string> = { Sent: 'ok', Test: 'warn', Queued: 'info' };
// Email Settings does nothing yet: that screen doesn't exist

const pct = (n?: number) => n == null ? '—' : `${n.toFixed(1)}%`;

const EmailCampaigns: React.FC = () => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const history = useHistory();
    const [summary, setSummary] = useState<CommunicationsCampaignsSummaryResult | null>(null);
    const [result, setResult] = useState<CommunicationsCampaignsResult | null>(null);
    const [error, setError] = useState('');
    const [input, setInput] = useState('');
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('All');
    const [type, setType] = useState('All');
    const [tests, setTests] = useState('Show');
    const [page, setPage] = useState(1);

    // search hits the server, so wait for typing to pause
    useEffect(() => {
        const t = setTimeout(() => { setSearch(input.trim()); setPage(1); }, 300);
        return () => clearTimeout(t);
    }, [input]);

    useAsyncEffect(async () => {
        if (!stock?.id) return;
        setSummary(null);
        const [err, data] = await to(api().getCommunicationsCampaignsSummary(stock.id));
        if (err) setError(err.message || 'Failed to load campaigns');
        else setSummary(data);
    }, [stock?.id]);

    useAsyncEffect(async () => {
        if (!stock?.id) return;
        const [err, data] = await to(api().getCommunicationsCampaigns(stock.id, {
            page, pageSize,
            search: search || undefined,
            status: status === 'All' ? undefined : status,
            type: type === 'All' ? undefined : type,
            hideTests: tests === 'Hide',
        }));
        if (err) setError(err.message || 'Failed to load campaigns');
        else { setError(''); setResult(data); }
    }, [stock?.id, page, search, status, type, tests]);

    const total = result?.total ?? 0;
    const rows = result?.rows || [];
    const pages = Math.max(1, Math.ceil(total / pageSize));
    const first = (page - 1) * pageSize;
    const resetPage = (set: (v: string) => void) => (v: string) => { set(v); setPage(1); };

    const select = (label: string, value: string, options: string[], set: (v: string) => void) =>
        <label className="filter">
            <select value={value} onChange={e => resetPage(set)(e.target.value)}>{options.map(o => <option key={o} value={o}>{label}: {o}</option>)}</select>
            <FiChevronDown />
        </label>;

    const delta = (previous?: number, n?: number) => previous != null && n != null && <span className={`pill ${n >= 0 ? 'ok' : 'bad'}`}>vs {pct(previous)} last quarter</span>;

    const rate = (n?: number) => n == null
        ? <span className="num">—</span>
        : <span className="num rate">{pct(n)}<span className="bar"><span style={{ width: `${Math.min(100, n)}%` }} /></span></span>;

    const Row = ({ r }: { r: CampaignRow }) => {
        const d = moment(r.created);
        const meta = r.status === 'Test' ? 'Test send' : [r.type, r.templateName && `${r.templateName} template`].filter(Boolean).join(' · ');
        return <div className="row">
            <div className="date"><div>{d.format('D MMM YYYY')}</div><div className="sub">{d.fromNow()}</div></div>
            <div className="headline">
                <div className="h">{r.subject}</div>
                <div className="meta">{meta}</div>
            </div>
            <span><span className={`pill ${tones[r.status] || ''}`}>{r.status}</span></span>
            <span className="num">{r.delivered == null ? '—' : r.delivered.toLocaleString()}</span>
            {rate(r.openedPct)}
            {rate(r.clickedPct)}
            <button type="button" className="view" onClick={() => history.push(`/communications/campaign/${encodeURIComponent(r.id)}${r.status === 'Draft' ? '' : '/details'}`)}>View <FiArrowRight /></button>
        </div>;
    };

    return <div className="announcements email-campaigns">
        <div className="page-header">
            <div>
                <h1>Email Campaigns</h1>
                <p>Emails you write yourself — updates, invitations and newsletters to your investor lists</p>
            </div>
            <div className="actions">
                <button type="button" className="btn-outline"><FiSliders /> Email Settings</button>
                <button type="button" className="btn-primary" onClick={() => history.push('/communications/campaign/new')}><FiPlus /> New Campaign</button>
            </div>
        </div>

        {error && <p className="error">{error}</p>}

        {summary && <div className="stats">
            <div className="stat">
                <div className="h"><span className="icon-tile"><FiSend /></span> Campaigns sent</div>
                <div className="v">{summary.sent.toLocaleString()}</div>
            </div>
            <div className="stat">
                <div className="h"><span className="icon-tile"><FiMail /></span> Average open rate {delta(summary.openRatePrevious, summary.openRateDelta)}</div>
                <div className="v">{pct(summary.openRate)}</div>
            </div>
            <div className="stat">
                <div className="h"><span className="icon-tile"><FiTrendingUp /></span> Average click rate {delta(summary.clickRatePrevious, summary.clickRateDelta)}</div>
                <div className="v">{pct(summary.clickRate)}</div>
            </div>
            <div className="stat">
                <div className="h"><span className="icon-tile"><FiUsers /></span> Subscribed contacts</div>
                <div className="v">{summary.subscribed.toLocaleString()}</div>
                <div className="d">of {summary.contacts.toLocaleString()} · {summary.unsubscribed.toLocaleString()} unsubscribed</div>
            </div>
        </div>}

        <div className="card">
            <div className="filters">
                <label className="search"><FiSearch /><input placeholder="Search subject…" value={input} onChange={e => setInput(e.target.value)} /></label>
                {select('Status', status, ['All', 'Sent', 'Test', 'Queued', 'Draft'], setStatus)}
                {select('Type', type, ['All', 'Custom', 'Announcement'], setType)}
                {select('Test Sends', tests, ['Show', 'Hide'], setTests)}
                <span className="count">Sorted by created, newest first</span>
            </div>

            <div className="table">
                <div className="row head">
                    <span>Created</span><span>Subject</span><span>Status</span><span className="num">Sent</span><span className="num">Opened</span><span className="num">Clicked</span><span />
                </div>
                {!result && !error && <p className="loading">Loading campaigns…</p>}
                {rows.map(r => <Row key={r.id} r={r} />)}
                {result && rows.length === 0 && <p className="empty">No campaigns match</p>}
            </div>

            <div className="footer">
                <span className="count">Showing {rows.length ? first + 1 : 0}–{first + rows.length} of {total.toLocaleString()} campaigns</span>
                <div className="pagination">
                    <button type="button" disabled={page === 1} onClick={() => setPage(page - 1)}>‹</button>
                    {Array.from({ length: pages }, (_, i) => i + 1).map(p =>
                        <button type="button" key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>)}
                    <button type="button" disabled={page === pages} onClick={() => setPage(page + 1)}>›</button>
                </div>
            </div>
        </div>
    </div>;
};

export default EmailCampaigns;
